import { unref } from 'vue';
import { CreateOrderResponse } from '@libs/api-reference';
import { useMutation, UseMutationConfig } from '@libs/use';

import * as API from '../requests';
import { ORDER_SIDES } from '../constants';

export type CreateOrderFormModel = {
  quantity: number;
  price: number;
  totalValue: number;
  executedQuantity?: number;
  validityDate?: Date;
  validityType?: number;
  remainingQuantity?: number | null;
  marginSource?: { value: number; label: string } | null;
  strategyLegs?: { value: string; label: string } | null;
};

type OrderFactoryOptions = {
  formModel: CreateOrderFormModel;
  orderExecutionType: string;
  symbol: string;
  type: 'buy' | 'sell';
  deviceType: number;
};

function orderFactory({
  formModel,
  orderExecutionType,
  symbol,
  type,
  deviceType,
}: OrderFactoryOptions) {
  return {
    orderExecutionType,
    instrumentIdentification: symbol,
    orderSide: ORDER_SIDES[type],
    parentId: 0,
    investorBourseCodeId: 0,
    validateAssetOnSell: false,
    quantity: formModel.quantity,
    price: formModel.price,
    validityDate: formModel.validityDate,
    validityType: formModel.validityType,
    deviceType,
    marginSource: formModel?.marginSource?.value,
    strategyLegs: formModel.strategyLegs
      ? JSON.stringify([{ isin: formModel.strategyLegs.value, index: 1 }])
      : null,
  };
}

type UseCreateOrderOptions = {
  symbol: string;
  orderExecutionType: string;
  type: OrderFactoryOptions['type'];
  deviceType: number;
  onReject?: UseMutationConfig<CreateOrderResponse>['onReject'];
  onSuccess?: UseMutationConfig<CreateOrderResponse>['onSuccess'];
};

export default function useCreateOrderQuery({
  orderExecutionType,
  symbol,
  type,
  deviceType,
  onReject,
  onSuccess,
}: UseCreateOrderOptions) {
  const {
    execute: createOrder,
    isPending: isLoadingCreateOrder,
    ...query
  } = useMutation<CreateOrderResponse>(
    ({ formModel }: { formModel: CreateOrderFormModel }) =>
      API.createOrder(
        orderFactory({
          formModel,
          symbol: unref(symbol),
          type,
          deviceType,
          orderExecutionType,
        })
      ),
    {
      onSuccess,
      onReject,
    }
  );

  return { createOrder, isLoadingCreateOrder, ...query };
}
